import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { produce } from 'immer'
import { find, map, remove } from 'lodash'
import dateUtil from '../utils/date.util'
import { VendorData } from '../apis/vendor.api'
import { ContactSiteData } from '../apis/contactSite.api'
import purchaseOrderApi, {
  PurchaseOrderCreateRequest
} from '../apis/purchaseOrder.api'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import Card from '../components/Card'
import DatePicker from '../components/DatePicker/DatePicker'
import CardButton from '../components/CardButton'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import PurchaseOrderItemInput, {
  PurchaseOrderItemInputItem
} from '../partials/PurchaseOrderItemInput'
import VendorAndSiteSelect from '../components/VendorAndSiteSelect'
import { Link } from 'react-router-dom'
import Button from '../components/Button'
import routes from '../routes/routes'

interface PurchaseOrderEnterProps {}

interface PurchaseOrderEnterState {
  purchaseOrder: string
  line: string
  deliverAt: string
  vendor: string
  vendorId: number | null
  vendorSiteId: string
  lineQuantity: string
  items: PurchaseOrderItemInputItem[],
  vendors: VendorData[],
  vendorSites: ContactSiteData[]
}

export default class PurchaseOrderEnter
  extends Component<PurchaseOrderEnterProps, PurchaseOrderEnterState> {
  constructor (props: PurchaseOrderEnterProps) {
    super(props)
    this.state = {
      purchaseOrder: '',
      line: '',
      vendor: '',
      vendorId: null,
      vendorSiteId: '',
      lineQuantity: '',
      deliverAt: this.timeNow,
      items: [],
      vendors: [],
      vendorSites: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
    this.handleItemAdd = this.handleItemAdd.bind(this)
    this.handleItemDelete = this.handleItemDelete.bind(this)
  }

  get timeNow () {
    return dateUtil.now()
  }

  get itemInput () {
    const { vendorSiteId } = this.state
    if (!vendorSiteId) return null
    return (
      <PurchaseOrderItemInput
        vendorSiteId={Number(vendorSiteId)}
        selecteds={this.state.items}
        onAdd={this.handleItemAdd}
        oneDelete={this.handleItemDelete} />
    )
  }

  create () {
    const { purchaseOrder, line, vendorSiteId, deliverAt } = this.state
    const items = map(this.state.items, item => {
      const { id, quantity } = item
      return { id, quantity: Number(quantity) }
    })
    const payload: PurchaseOrderCreateRequest = {}
    if (purchaseOrder) payload.purchaseOrder = purchaseOrder
    if (line) payload.line = line
    if (vendorSiteId) payload.vendorSite = Number(vendorSiteId)
    if (items) payload.items = items
    if (deliverAt) payload.deliveryAt = deliverAt
    purchaseOrderApi.create(payload, purchaseOrders => {
      if (!purchaseOrders) return
      alertService.success('Successfully entered purchase order details')
      this.resetFields()
    })
  }

  resetFields () {
    const defaultState: PurchaseOrderEnterState = {
      purchaseOrder: '',
      line: '',
      vendor: '',
      vendorId: null,
      vendorSiteId: '',
      lineQuantity: '',
      deliverAt: this.timeNow,
      items: [],
      vendors: [],
      vendorSites: []
    }
    this.setState(defaultState)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'purchaseOrder') this.setState({ purchaseOrder: value })
    else if (name === 'line') this.setState({ line: value })
    else if (name === 'lineQuantity') this.setState({ lineQuantity: value })
  }

  handleDateChange (name: string, date: string) {
    if (name === 'deliverAt') this.setState({ deliverAt: date })
  }

  handleItemAdd (item: PurchaseOrderItemInputItem) {
    const items = produce(this.state.items, items => {
      const existingItem = find(items, { id: item.id })
      if (existingItem) remove(items, { id: item.id })
      items.push(item)
    })
    this.setState({ items })
  }

  handleItemDelete (id: number) {
    const items = produce(this.state.items, items => {
      remove(items, item => (item.id === id))
    })
    this.setState({ items })
  }

  handleCreateClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review the purchase order details above and then confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.create()
    }
    confirmService.push(confirm)
  }

  handleVendorsChange = (vendors: VendorData[]) => {
    this.setState({ vendors })
  }

  handleVendorSitesChange = (vendorSites: ContactSiteData[]) => {
    this.setState({ vendorSites })
  }

  handleVendorIdChange = (vendorId: number | null) => {
    this.setState({ vendorId })
  }

  handleVendorSiteIdChange = (vendorSiteId: string) => {
    this.setState({ vendorSiteId })
  }

  handleVendorChange = (vendor: string) => {
    this.setState({ vendor })
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Enter Purchase Order Details")}
              doc='purchase-orders-create-purchase-order' />
            <Row column={2}>
              <InputText
                name="purchaseOrder"
                value={this.state.purchaseOrder}
                label={i18n("Purchase order")}
                onChange={this.handleInputChange} />
              <InputText
                name="line"
                value={this.state.line}
                label={i18n("Line")}
                onChange={this.handleInputChange} />
              <DatePicker
                name="deliverAt"
                value={this.state.deliverAt}
                label={i18n("Delivery date")}
                onChange={this.handleDateChange} />
            </Row>
            <Row column={2}>
            <VendorAndSiteSelect
                vendor={this.state.vendor}
                vendorSiteId={this.state.vendorSiteId}
                vendors={this.state.vendors}
                vendorSites={this.state.vendorSites}
                onVendorIdChange={this.handleVendorIdChange}
                onVendorSiteIdChange={this.handleVendorSiteIdChange}
                onVendorChange={this.handleVendorChange}
                onVendorsChange={this.handleVendorsChange}
                onVendorSitesChange={this.handleVendorSitesChange} />
              <Link to={routes.contactCreate} className="flex flex-row-reverse">
                <Button text={i18n("Add Vendor Contact")} onClick={()=>{}} />
              </Link>
            </Row>
            <Row column={1}>
              {this.itemInput}
            </Row>
            <CardButton
              proceedText={i18n("Create")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleCreateClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}
